import { useEffect, useState, useCallback } from "react";

const DISMISS_KEY = "moodcard.install.dismissed.v1";
const VISIT_KEY = "moodcard.install.visits.v1";
const SUPPRESS_DAYS = 7;
const MIN_VISITS = 2;

function isIos() {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}

function bumpVisitCount() {
    try {
        const n = parseInt(localStorage.getItem(VISIT_KEY) || "0", 10) + 1;
        localStorage.setItem(VISIT_KEY, String(n));
        return n;
    } catch {
        return 1;
    }
}

function isDismissedRecently() {
    try {
        const v = localStorage.getItem(DISMISS_KEY);
        if (!v) return false;
        // Legacy "1" → treat as dismissed forever (back-compat)
        if (v === "1") return true;
        const at = parseInt(v, 10);
        if (!at) return false;
        const days = (Date.now() - at) / (24 * 60 * 60 * 1000);
        return days < SUPPRESS_DAYS;
    } catch {
        return false;
    }
}

export function usePWAInstall() {
    const [deferred, setDeferred] = useState(null);
    const [canInstall, setCanInstall] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    // iter-77: iOS-specific path. iOS Safari never fires `beforeinstallprompt`,
    // so we expose a flag so the banner can render an "add to home screen" hint
    // sheet instead of trying to call deferred.prompt().
    const [iosCanInstall, setIosCanInstall] = useState(false);

    useEffect(() => {
        const standalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        setIsStandalone(standalone);
        if (standalone) return;

        if (isDismissedRecently()) return;

        const visits = bumpVisitCount();

        // ── Chrome / Edge / Android path ─────────────────────────────
        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
            if (visits >= MIN_VISITS) setCanInstall(true);
        };
        const onInstalled = () => {
            setCanInstall(false);
            setIosCanInstall(false);
            setDeferred(null);
            try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        };

        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);

        // ── iOS Safari path ──────────────────────────────────────────
        // No `beforeinstallprompt` ever fires. After MIN_VISITS, surface the
        // banner that opens a "share → add to home screen" sheet.
        if (isIos() && visits >= MIN_VISITS) {
            setIosCanInstall(true);
        }

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const install = useCallback(async () => {
        if (!deferred) return false;
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        setDeferred(null);
        setCanInstall(false);
        if (outcome !== "accepted") {
            try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        }
        return outcome === "accepted";
    }, [deferred]);

    const dismiss = useCallback(() => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setCanInstall(false);
        setIosCanInstall(false);
    }, []);

    return {
        canInstall: canInstall || iosCanInstall,
        isIosInstall: iosCanInstall && !canInstall,
        install,
        dismiss,
        isStandalone,
    };
}
